import { useCallback, useContext } from 'react'

import useSetImmersiveMode from './useImmersiveModeHandlers'
import useTripClientSource from './useTripClientSource'
import BookHotelFlights from '../components/TripModal/RestoreCart/BookHotelFlight'

import { BaseButtonProps } from 'components/Luxkit/Button/BaseButton'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import noop from 'lib/function/noop'
import { ItemCardVariant } from 'tripPlanner/components/ItemCard/ItemCard'
import BookTourFlight from 'tripPlanner/components/TripModal/RestoreCart/BookTourFlight'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience, useOffer } from 'tripPlanner/hooks/api'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { LETripItem } from 'tripPlanner/types/tripItem'
import { getRestoreCartButtonCopy } from 'tripPlanner/utils/bookmark/buttonCopy'
import {
  buildRestoreCartURLWithQueryParams,
  buildProductLinkWithQueryParams,
  isBookableHotelFlightBundle,
  isBookableTourFlightBundle,
} from 'tripPlanner/utils/bookmark/restoreCart'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export const BOOK_NOW_BUTTON_TEXT = 'Book now'

type ReturnProps = {
  label: string | undefined
  onClick: Exclude<BaseButtonProps['onClick'], undefined>
  restoreCartUrl: BaseButtonProps['to']
  productLink: string | undefined
}

const offerSourceTypes = new Set<LETripItem['sourceType']>([
  'LE_Hotel',
  'LE_Hotel_Bedbank',
  'LE_Tour',
  'LE_Cruise',
])

export default function useRestoreCartButtonProps(
  item: LETripItem,
  variant: ItemCardVariant,
  curatedTripId?: string,
): ReturnProps {
  let offerId: string | undefined = undefined
  if (offerSourceTypes.has(item.sourceType)) {
    offerId = item.code
  } else if (item.type === 'FLIGHT' && item.savedItemData?.hotelBundle) {
    offerId = item.savedItemData.hotelBundle.offerId
  } else if (item.type === 'FLIGHT' && item.savedItemData?.tourBundle) {
    offerId = item.savedItemData.tourBundle.offerId
  }
  const { data: offer } = useOffer(offerId, {
    isCruise: item.sourceType === 'LE_Cruise',
  })
  const { data: experience } = useExperience({
    currentCurrency: item.savedItemData.currencyCode,
    currentRegionCode: item.savedItemData.regionCode,
    experienceId: item.sourceType === 'LE_Experience' ? item.code : undefined,
  })

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit

  const { setImmersiveMode } = useSetImmersiveMode()
  const openModal = useOpenModal()

  const source = useTripClientSource()
  const restoreCartUrl = buildRestoreCartURLWithQueryParams(
    item,
    offer,
    experience,
    curatedTripId,
    { source },
  )
  const modalElement = useModalElementContext()
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      const canBookOfferStandalone =
        offer && !(offer as App.Offer).bundledWithFlightsOnly

      if (restoreCartUrl && canBookOfferStandalone) {
        if (isBookableHotelFlightBundle(item)) {
          e.preventDefault()
          e.stopPropagation()
          openModal(BookHotelFlights, { item, restoreCartUrl })
        } else if (isBookableTourFlightBundle(item)) {
          e.preventDefault()
          e.stopPropagation()
          openModal(BookTourFlight, { item, restoreCartUrl })
        } else {
          if (canEdit) {
            setImmersiveMode({
              tripId: item.tripId,
              sectionId: itemSectionId(item.id),
            })
          }
        }
      } else {
        if (canEdit) {
          setImmersiveMode({
            tripId: item.tripId,
            sectionId: itemSectionId(item.id),
          })
        }
      }
      modalElement?.resolve()
    },
    [
      offer,
      restoreCartUrl,
      modalElement,
      item,
      openModal,
      canEdit,
      setImmersiveMode,
    ],
  )

  if (offerId && (!offer || (offer as App.Offer).bundledWithFlightsOnly)) {
    // We shouldn't show book now button for bundle-only offers, it's too complicated to make it work right.
    // If we have an offerId but no offer, we don't yet know if the offer is bundle-only.
    // In this case we shouldn't render the button.
    return {
      label: undefined,
      restoreCartUrl: undefined,
      onClick: noop,
      productLink: buildProductLinkWithQueryParams(
        item,
        !viewAsTemplate,
        { offer, experience },
        { source },
      ),
    }
  }

  return {
    label:
      variant === 'drawer'
        ? BOOK_NOW_BUTTON_TEXT
        : getRestoreCartButtonCopy(item),
    restoreCartUrl,
    onClick,
    productLink: buildProductLinkWithQueryParams(
      item,
      !viewAsTemplate,
      { offer, experience },
      { source },
    ),
  }
}
